'use client'

import { useEffect, useState } from 'react'
import type { MarketplacePriceSourceType } from '@/lib/marketplace/types'

const DEFAULT_CRATE_CART_FALLBACK_URL = '/search'

export interface CrateCartDisplay {
  name: string
  amount: number
  currency: string
  priceLabel: string
  typeLabel: string
  vertical?: string | null
  preview_url?: string | null
  previewUrl?: string | null
  thumbnail_url?: string | null
  thumbnailUrl?: string | null
  waveform_url?: string | null
  waveformUrl?: string | null
}

export interface CrateCartSourceContext {
  explicitPurchase: boolean
  originUrl: string
  originDomain: string
}

export interface CrateCartAssetInput {
  type: 'asset'
  assetUuid: string
  assetHref?: string | null
  assetPriceId?: number
  assetPriceSourceType?: Extract<MarketplacePriceSourceType, 'asset_price' | 'vertical_asset_price'>
  display: CrateCartDisplay
  sourceContext: CrateCartSourceContext
}

export interface CrateCartBundleInput {
  type: 'bundle'
  bundleId: number
  display: CrateCartDisplay
  sourceContext: CrateCartSourceContext
}

export type CrateCartAddItemInput = CrateCartAssetInput | CrateCartBundleInput

export interface CrateCartLine {
  lineKey?: string
  type: 'asset' | 'bundle'
  assetUuid?: string
  bundleId?: number
}

export interface CrateCartSnapshot {
  isReady?: boolean
  lines: CrateCartLine[]
  syncState?: 'clean' | 'dirty' | 'syncing' | 'error'
}

export interface CrateCartAssetOpenDetail {
  assetUuid: string | null
  assetHref: string | null
  lineKey: string
  source: 'marketplace-cart'
}

export interface CrateCartApi {
  addItem: (input: CrateCartAddItemInput) => Promise<unknown>
  removeItem: (lineKey: string) => Promise<unknown>
  open: () => void
  close: () => void
  clearAfterCheckout?: () => Promise<unknown>
  getSnapshot: () => CrateCartSnapshot
  sync: (input: { reason: string }) => Promise<unknown>
  subscribe: (listener: (snapshot: CrateCartSnapshot) => void) => (() => void) | void
}

declare global {
  interface Window {
    CrateCart?: CrateCartApi
  }
}

export async function waitForCrateCart(timeoutMs = 5000): Promise<CrateCartApi> {
  const start = Date.now()

  return new Promise((resolve, reject) => {
    const tryResolve = () => {
      const cart = window.CrateCart
      if (!cart?.getSnapshot) {
        return false
      }

      const snapshot = cart.getSnapshot()
      if (snapshot?.isReady) {
        resolve(cart)
        return true
      }

      return false
    }

    if (tryResolve()) {
      return
    }

    const onReady = () => {
      cleanup()
      if (window.CrateCart) {
        resolve(window.CrateCart)
        return
      }
      reject(new Error('CrateCart became ready without a public API'))
    }

    const intervalId = window.setInterval(() => {
      if (tryResolve()) {
        cleanup()
        return
      }

      if (Date.now() - start > timeoutMs) {
        cleanup()
        reject(new Error('CrateCart did not become ready in time'))
      }
    }, 50)

    const cleanup = () => {
      window.clearInterval(intervalId)
      window.removeEventListener('crate:cart:ready', onReady)
    }

    window.addEventListener('crate:cart:ready', onReady)
  })
}

export function getCrateCartSnapshot(): CrateCartSnapshot | null {
  if (typeof window === 'undefined') {
    return null
  }

  return window.CrateCart?.getSnapshot() ?? null
}

export function getCrateCartAssetLineKey(assetUuid: string): string {
  return `asset:${assetUuid}`
}

export function getCrateCartBundleLineKey(bundleId: number): string {
  return `bundle:${bundleId}`
}

export function hasCrateCartLine(snapshot: CrateCartSnapshot | null | undefined, lineKey: string): boolean {
  if (!snapshot?.lines?.length) {
    return false
  }

  return snapshot.lines.some((line) => {
    if (line.lineKey === lineKey) {
      return true
    }

    if (line.type === 'asset' && line.assetUuid) {
      return getCrateCartAssetLineKey(line.assetUuid) === lineKey
    }

    if (line.type === 'bundle' && typeof line.bundleId === 'number') {
      return getCrateCartBundleLineKey(line.bundleId) === lineKey
    }

    return false
  })
}

export function useCrateCartSnapshot() {
  const [snapshot, setSnapshot] = useState<CrateCartSnapshot | null>(() => getCrateCartSnapshot())

  useEffect(() => {
    let isMounted = true
    let unsubscribe: (() => void) | void

    const updateSnapshot = () => {
      if (!isMounted) {
        return
      }

      setSnapshot(getCrateCartSnapshot())
    }

    updateSnapshot()

    void waitForCrateCart()
      .then((cart) => {
        if (!isMounted) {
          return
        }

        updateSnapshot()
        unsubscribe = cart.subscribe((nextSnapshot) => {
          if (isMounted) {
            setSnapshot(nextSnapshot)
          }
        })
      })
      .catch(() => {
        updateSnapshot()
      })

    window.addEventListener('crate:cart:ready', updateSnapshot)
    window.addEventListener('crate:cart:changed', updateSnapshot)

    return () => {
      isMounted = false
      unsubscribe?.()
      window.removeEventListener('crate:cart:ready', updateSnapshot)
      window.removeEventListener('crate:cart:changed', updateSnapshot)
    }
  }, [])

  return snapshot
}

export async function openCrateCart({
  timeoutMs = 5000,
  fallbackUrl = DEFAULT_CRATE_CART_FALLBACK_URL,
  redirectOnFailure = false,
}: {
  timeoutMs?: number
  fallbackUrl?: string
  redirectOnFailure?: boolean
} = {}): Promise<boolean> {
  try {
    const cart = await waitForCrateCart(timeoutMs)
    cart.open()
    return true
  } catch (error) {
    if (redirectOnFailure) {
      window.location.assign(fallbackUrl)
    }

    return false
  }
}

export async function clearCrateCartAfterCheckout(timeoutMs = 5000): Promise<boolean> {
  try {
    const cart = await waitForCrateCart(timeoutMs)
    if (typeof cart.clearAfterCheckout === 'function') {
      await cart.clearAfterCheckout()
      return true
    }

    return false
  } catch (error) {
    return false
  }
}
